import {
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  usePortfolioDetail,
  usePortfolioSummary,
  useUpdatePortfolio,
} from "../../hooks/queries/usePortfolios";
import {
  Portfolio,
  portfolioApi,
  PortfolioItem,
} from "../../services/portfolioApi";
import LogoLoader from "../common/LogoLoader";
import EdgeDetailOverlay from "../Layout/EdgeDetailOverlay";
import EntityDetailOverlay from "../Layout/EntityDetailOverlay";
import BulkAddToPortfolioButton from "./BulkAddToPortfolioButton";
import BulkDeleteButton from "./BulkDeleteButton";
import BulkEditPortfolioItemsButton from "./BulkEditPortfolioItemsButton";
import PortfolioSidebar from "./PortfolioSidebar";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`portfolio-tabpanel-${index}`}
      aria-labelledby={`portfolio-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const entityTypes = [
  { key: "organization" as const, label: "Organizations", color: "#1976d2" },
  { key: "facility" as const, label: "Facilities", color: "#388e3c" },
  { key: "project" as const, label: "Projects", color: "#f57c00" },
  { key: "source" as const, label: "Sources", color: "#7b1fa2" },
];

const PortfolioView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const portfolioId = searchParams.get("portfolio_id");

  // ✅ Fetch portfolio detail + summary via TanStack Query
  const portfolioQuery = usePortfolioDetail(portfolioId ?? undefined);
  const summaryQuery = usePortfolioSummary(portfolioId ?? undefined);
  const updateMutation = useUpdatePortfolio();

  const portfolio = portfolioQuery.data ?? null;
  const entityCounts = summaryQuery.data?.counts ?? {};
  const loading = portfolioQuery.isLoading;

  const [allItems, setAllItems] = useState<PortfolioItem[]>([]);
  const [enrichedData, setEnrichedData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loadingTabData, setLoadingTabData] = useState(false);
  const [hydratingPage, setHydratingPage] = useState(false);
  const [tablePagination, setTablePagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState(portfolio?.name ?? "");
  const [editDescription, setEditDescription] = useState(
    portfolio?.description ?? "",
  );

  // Sync edit fields when portfolio data loads
  useEffect(() => {
    if (portfolio) {
      setEditName(portfolio.name);
      setEditDescription(portfolio.description || "");
    }
  }, [portfolio]);

  // Load items for the active tab when portfolio data is ready
  useEffect(() => {
    if (portfolioId && portfolio) {
      loadItemsForTab(activeTab);
    }
  }, [portfolioId, portfolio]);

  const loadItemsForTab = useCallback(
    async (tabIndex: number) => {
      if (!portfolioId) return;

      try {
        setLoadingTabData(true);
        const entityType = entityTypes[tabIndex].key;

        // Fetch ALL portfolio items for this entity type by paginating through all pages
        let allPortfolioItems: PortfolioItem[] = [];
        let page = 1;
        let hasMoreItems = true;

        while (hasMoreItems) {
          const itemsResponse = await portfolioApi.getPortfolioItems(
            portfolioId,
            {
              entity_type: entityType,
              page: page,
              limit: 100, // Use reasonable page size
            },
          );

          allPortfolioItems = [...allPortfolioItems, ...itemsResponse.results];
          hasMoreItems = !!itemsResponse.next;
          page++;
        }

        setAllItems(allPortfolioItems);

        // Create initial enriched data with portfolio item data but no hydrated entity data
        const initialEnrichedData = allPortfolioItems.map((portfolioItem) => ({
          id: portfolioItem.entity_id,
          name: `Loading...`, // Placeholder
          portfolio_notes: portfolioItem.notes,
          portfolio_labels: portfolioItem.labels,
          portfolio_pin_rank: portfolioItem.pin_rank,
          portfolio_added_by: portfolioItem.added_by,
          portfolio_added_date: portfolioItem.created_at,
          portfolio_item_id: portfolioItem.id,
          _isHydrated: false,
        }));

        setEnrichedData(initialEnrichedData);

        // Hydrate the first page
        await hydrateCurrentPage(
          allPortfolioItems,
          entityType,
          0,
          tablePagination.pageSize,
        );
      } catch (error) {
        setAllItems([]);
        setEnrichedData([]);
      } finally {
        setLoadingTabData(false);
      }
    },
    [portfolioId, tablePagination.pageSize],
  );

  const hydrateCurrentPage = useCallback(
    async (
      items: PortfolioItem[],
      entityType: "organization" | "facility" | "project" | "source",
      pageIndex: number,
      pageSize: number,
    ) => {
      if (!portfolioId || items.length === 0) return;

      try {
        setHydratingPage(true);

        // Calculate which items are on the current page
        const startIndex = pageIndex * pageSize;
        const endIndex = Math.min(startIndex + pageSize, items.length);
        const pageItems = items.slice(startIndex, endIndex);

        // Get entity IDs for current page
        const entityIds = pageItems.map((item) => item.entity_id);

        if (entityIds.length === 0) {
          return;
        }

        // Hydrate only the entities for the current page
        const hydratedEntities = await portfolioApi.hydrateSpecificEntities(
          portfolioId,
          entityType,
          entityIds,
        );

        // Update enriched data by replacing the hydrated items
        setEnrichedData((prevData) => {
          const newData = [...prevData];
          pageItems.forEach((portfolioItem) => {
            const hydratedEntity = hydratedEntities.find(
              (entity) => entity.id === portfolioItem.entity_id,
            );
            const dataIndex = newData.findIndex(
              (item) => item.id === portfolioItem.entity_id,
            );

            if (dataIndex !== -1 && hydratedEntity) {
              newData[dataIndex] = {
                ...hydratedEntity,
                // Add portfolio-specific fields directly to the row data
                portfolio_notes: portfolioItem.notes,
                portfolio_labels: portfolioItem.labels,
                portfolio_pin_rank: portfolioItem.pin_rank,
                portfolio_added_by: portfolioItem.added_by,
                portfolio_added_date: portfolioItem.created_at,
                portfolio_item_id: portfolioItem.id,
                _isHydrated: true,
              };
            }
          });
          return newData;
        });
      } catch (error) {
        return;
      } finally {
        setHydratingPage(false);
      }
    },
    [portfolioId],
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setAllItems([]);
    setEnrichedData([]);
    setTablePagination({ pageIndex: 0, pageSize: 25 });
    setSelectedRows({});
    loadItemsForTab(newValue);
  };

  const handleTablePaginationChange = (updater: any) => {
    const newPagination =
      typeof updater === "function" ? updater(tablePagination) : updater;
    setTablePagination(newPagination);

    // Hydrate the new page
    const entityType = entityTypes[activeTab].key;
    hydrateCurrentPage(
      allItems,
      entityType,
      newPagination.pageIndex,
      newPagination.pageSize,
    );
  };

  // Remove handleLoadMore - no longer needed with client-side pagination

  const handleEditSave = async () => {
    if (!portfolio) return;

    try {
      await updateMutation.mutateAsync({
        id: portfolio.id,
        data: {
          name: editName,
          description: editDescription,
        },
      });
      setEditDialogOpen(false);
    } catch (error) {
      return;
    }
  };

  // Entity click handler for opening detail view
  const handleEntityClick = useCallback(
    (entityId: string) => {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_relationship_id"); // Clear relationship overlay
      params.set("detail_entity_id", entityId);
      setSearchParams(params, { replace: false }); // Create history entry
    },
    [searchParams, setSearchParams],
  );

  // Handler to close entity detail overlay
  const handleCloseEntityDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    setSearchParams(params, { replace: false });
  };

  // Handler to close relationship detail overlay
  const handleCloseRelationshipDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id");
    setSearchParams(params, { replace: false });
  };

  // Handler for entity navigation within overlay
  const handleEntityNavigation = (entityId: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id"); // Clear relationship overlay
    params.set("detail_entity_id", entityId);
    setSearchParams(params, { replace: false });
  };

  // Handler for opening entity in graph view
  const handleOpenInGraph = (entityId: string) => {
    // Navigate to graph view with the entity as seed, without detail overlay
    const graphParams = new URLSearchParams();
    graphParams.set("seed_entity", entityId);
    graphParams.set("org_structure", "parents_subsidiaries");
    navigate(`/graph?${graphParams.toString()}`);
  };

  const handleOpenInMap = (
    entityName: string,
    entityType: string,
    entityId: string,
  ) => {
    if (entityType === "organization") {
      // For organizations, navigate to map with facility owner filter
      const facilityOwnerData = JSON.stringify([
        { id: entityId, name: entityName },
      ]);
      navigate(
        `/map?facilityOwnerCompanies=${encodeURIComponent(facilityOwnerData)}&facilityOwnerMaxTiers=1`,
      );
    } else {
      // For facilities and other entities, use search term
      navigate(`/map?search=${encodeURIComponent(entityName)}`);
    }
  };

  // Handle clicking anywhere on the page to close overlay (except on interactive elements)
  const handlePageClick = (event: React.MouseEvent) => {
    // Don't close overlay if clicking on sidebar or other interactive elements
    const target = event.target as HTMLElement;

    // Check if the click is on the sidebar or its children
    const sidebar = target.closest("[data-sidebar]");
    if (sidebar) {
      return; // Let sidebar navigation proceed without closing overlay
    }

    // Check if clicking on overlay content itself
    const overlayContent = target.closest("[data-overlay-content]");
    if (overlayContent) {
      return; // Don't close when clicking inside overlay
    }

    // Check if clicking on MUI portaled components (menus, popovers, dialogs, etc.)
    const isMuiPortal = target.closest(
      ".MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiTooltip-popper, .MuiAutocomplete-popper",
    );
    if (isMuiPortal) {
      return; // Don't close when clicking on MUI portaled components
    }

    // Only close if there's an overlay open and click is outside interactive elements
    if (
      searchParams.get("detail_entity_id") ||
      searchParams.get("detail_relationship_id")
    ) {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_entity_id");
      params.delete("detail_relationship_id");
      setSearchParams(params, { replace: false });
    }
  };

  const getHydratedColumns = useMemo<MRT_ColumnDef<any>[]>(() => {
    const entityType = entityTypes[activeTab].key;
    const baseColumns: MRT_ColumnDef<any>[] = [];

    if (entityType === "organization") {
      baseColumns.push(
        {
          accessorKey: "name",
          header: "Name",
          size: 200,
          Cell: ({ cell, row }) => (
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                cursor: "pointer",
                color: "text.primary",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.light",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEntityClick(row.original.id);
              }}
            >
              {String(cell.getValue() || "")}
            </Typography>
          ),
        },
        {
          accessorKey: "home_country",
          header: "Country",
          size: 80,
          Cell: ({ cell }) => (
            <Typography variant="body2">
              {String(cell.getValue() || "")}
            </Typography>
          ),
        },
        {
          accessorKey: "founding_year",
          header: "Founded",
          size: 80,
          Cell: ({ cell }) => {
            const year = cell.getValue() as number;
            return (
              <Typography variant="body2">
                {year ? year.toString() : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "portfolio_notes",
          header: "Notes",
          size: 200,
          Cell: ({ row }) => {
            const notes = row.original.portfolio_notes;
            return (
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontStyle: notes ? "normal" : "italic",
                  color: notes ? "text.primary" : "text.secondary",
                }}
              >
                {notes || ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "portfolio_labels",
          header: "Portfolio Labels",
          size: 140,
          Cell: ({ row }) => {
            const labels = row.original.portfolio_labels || [];
            if (labels.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.slice(0, 2).map((label: string, index: number) => (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    variant="filled"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                ))}
                {labels.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {labels.slice(2).map((label: string, idx: number) => (
                          <div key={idx}>{label}</div>
                        ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${labels.length - 2}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "portfolio_added_date",
          header: "Added",
          size: 90,
          Cell: ({ row }) => {
            return (
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                {row.original.portfolio_added_date
                  ? new Date(row.original.portfolio_added_date)
                      .toISOString()
                      .split("T")[0]
                  : ""}
              </Typography>
            );
          },
        },
      );
    } else if (entityType === "facility") {
      baseColumns.push(
        {
          accessorKey: "name",
          header: "Name",
          size: 200,
          Cell: ({ cell, row }) => (
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                cursor: "pointer",
                color: "text.primary",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.light",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEntityClick(row.original.id);
              }}
            >
              {String(cell.getValue() || "")}
            </Typography>
          ),
        },
        {
          accessorKey: "facility_type",
          header: "Type",
          size: 150,
          Cell: ({ cell }) => {
            const facilityType = cell.getValue() as string | string[];

            // Handle both single string and array of strings
            const types = Array.isArray(facilityType)
              ? facilityType
              : [facilityType].filter(Boolean);

            if (types.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {types.slice(0, 2).map((type: string, index: number) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
                {types.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {types.slice(2).map((type: string, idx: number) => (
                          <div key={idx}>{type}</div>
                        ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${types.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        // {
        //   accessorKey: 'address',
        //   header: 'Address',
        //   size: 100,
        //   Cell: ({ cell }) => (
        //     <Typography variant="body2">
        //       {String(cell.getValue() || '')}
        //     </Typography>
        //   ),
        // },
        {
          accessorKey: "country_iso2",
          header: "Country",
          size: 100,
          Cell: ({ cell }) => (
            <Typography variant="body2">
              {String(cell.getValue() || "")}
            </Typography>
          ),
        },
        {
          accessorKey: "status_labels",
          header: "Status",
          size: 120,
          Cell: ({ cell }) => {
            const statusLabels = cell.getValue() as string[] | string;
            const status =
              Array.isArray(statusLabels) && statusLabels.length > 0
                ? statusLabels[0]
                : typeof statusLabels === "string"
                  ? statusLabels
                  : "Unknown";
            return (
              <Chip
                label={status}
                size="small"
                variant="outlined"
                color={
                  status === "active"
                    ? "success"
                    : status === "inactive"
                      ? "error"
                      : "default"
                }
                sx={{ fontSize: "0.75rem" }}
              />
            );
          },
        },
        {
          accessorKey: "portfolio_notes",
          header: "Notes",
          size: 180,
          Cell: ({ row }) => {
            const notes = row.original.portfolio_notes;
            return (
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontStyle: notes ? "normal" : "italic",
                  color: notes ? "text.primary" : "text.secondary",
                }}
              >
                {notes || ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "portfolio_labels",
          header: "Portfolio Labels",
          size: 140,
          Cell: ({ row }) => {
            const labels = row.original.portfolio_labels || [];
            if (labels.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.slice(0, 2).map((label: string, index: number) => (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    variant="filled"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                ))}
                {labels.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {labels.slice(2).map((label: string, idx: number) => (
                          <div key={idx}>{label}</div>
                        ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${labels.length - 2}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "portfolio_added_date",
          header: "Added",
          size: 90,
          Cell: ({ row }) => {
            return (
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                {row.original.portfolio_added_date
                  ? new Date(row.original.portfolio_added_date)
                      .toISOString()
                      .split("T")[0]
                  : ""}
              </Typography>
            );
          },
        },
      );
    } else if (entityType === "project") {
      baseColumns.push(
        {
          accessorKey: "name",
          header: "Name",
          size: 200,
          Cell: ({ cell, row }) => (
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                cursor: "pointer",
                color: "text.primary",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.light",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEntityClick(row.original.id);
              }}
            >
              {String(cell.getValue() || "")}
            </Typography>
          ),
        },
        {
          accessorKey: "project_type",
          header: "Type",
          size: 120,
          Cell: ({ cell }) => {
            const projectType = cell.getValue() as string | string[];
            const types = Array.isArray(projectType)
              ? projectType
              : [projectType].filter(Boolean);
            if (types.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {types.slice(0, 1).map((type: string, index: number) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                ))}
                {types.length > 1 && (
                  <Tooltip
                    title={
                      <Box>
                        {types.slice(1).map((type: string, idx: number) => (
                          <div key={idx}>{type}</div>
                        ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${types.length - 1}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.7rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "project_status",
          header: "Status",
          size: 120,
          Cell: ({ cell }) => {
            const statusLabels = cell.getValue() as string[] | string;
            const status =
              Array.isArray(statusLabels) && statusLabels.length > 0
                ? statusLabels[0]
                : typeof statusLabels === "string"
                  ? statusLabels
                  : "Unknown";
            return (
              <Chip
                label={status}
                size="small"
                variant="outlined"
                color={
                  status === "ongoing"
                    ? "success"
                    : status === "cancelled"
                      ? "error"
                      : "default"
                }
                sx={{ fontSize: "0.75rem" }}
              />
            );
          },
        },
        {
          accessorKey: "publication_date",
          header: "Last Updated",
          size: 100,
          Cell: ({ cell }) => {
            const year = cell.getValue() as number;
            return (
              <Typography variant="body2">
                {year ? year.toString() : ""}
              </Typography>
            );
          },
        },
        // {
        //   accessorKey: 'project_start',
        //   header: 'Project Start',
        //   size: 100,
        //   Cell: ({ cell }) => {
        //     const year = cell.getValue() as number;
        //     return (
        //       <Typography variant="body2">
        //         {year ? year.toString() : ''}
        //       </Typography>
        //     );
        //   },
        // },
        // {
        //   accessorKey: 'project_end',
        //   header: 'Project End',
        //   size: 100,
        //   Cell: ({ cell }) => {
        //     const year = cell.getValue() as number;
        //     return (
        //       <Typography variant="body2">
        //         {year ? year.toString() : ''}
        //       </Typography>
        //     );
        //   },
        // },
        // {
        //   accessorKey: 'budget',
        //   header: 'Budget',
        //   size: 120,
        //   Cell: ({ row }) => {
        //     const value = row.original.project_volume_value;
        //     const currency = row.original.project_volume_currency;

        //     if (!value && value !== 0) {
        //       return <Typography variant="body2" color="text.secondary"></Typography>;
        //     }

        //     // Format the value with proper number formatting
        //     const formatValue = (val: number) => {
        //       if (val >= 1e9) {
        //         return `${(val / 1e9).toFixed(1)}B`;
        //       } else if (val >= 1e6) {
        //         return `${(val / 1e6).toFixed(1)}M`;
        //       } else if (val >= 1e3) {
        //         return `${(val / 1e3).toFixed(1)}K`;
        //       }
        //       return val.toLocaleString();
        //     };

        //     const formattedValue = formatValue(value);
        //     const displayText = currency ? `${currency} ${formattedValue}` : formattedValue;

        //     return (
        //       <Typography variant="body2">
        //         {displayText}
        //       </Typography>
        //     );
        //   },
        // },
        {
          accessorKey: "portfolio_notes",
          header: "Notes",
          size: 180,
          Cell: ({ row }) => {
            const notes = row.original.portfolio_notes;
            return (
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontStyle: notes ? "normal" : "italic",
                  color: notes ? "text.primary" : "text.secondary",
                }}
              >
                {notes || ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "portfolio_labels",
          header: "Portfolio Labels",
          size: 140,
          Cell: ({ row }) => {
            const labels = row.original.portfolio_labels || [];
            if (labels.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.slice(0, 2).map((label: string, index: number) => (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    variant="filled"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                ))}
                {labels.length > 2 && (
                  <Chip
                    label={`+${labels.length - 2}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem" }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "portfolio_added_date",
          header: "Added",
          size: 90,
          Cell: ({ row }) => {
            return (
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                {row.original.portfolio_added_date
                  ? new Date(row.original.portfolio_added_date)
                      .toISOString()
                      .split("T")[0]
                  : ""}
              </Typography>
            );
          },
        },
      );
    } else if (entityType === "source") {
      baseColumns.push(
        {
          accessorKey: "title",
          header: "Title",
          size: 250,
          Cell: ({ cell, row }) => (
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                cursor: "pointer",
                color: "text.primary",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.light",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEntityClick(row.original.id);
              }}
            >
              {String(cell.getValue() || "")}
            </Typography>
          ),
        },
        {
          accessorKey: "publication_date",
          header: "Published",
          size: 80,
          Cell: ({ cell }) => {
            const date = cell.getValue() as string;
            return (
              <Typography variant="body2">
                {date ? new Date(date).toISOString().split("T")[0] : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "domain",
          header: "Source",
          size: 80,
          Cell: ({ cell }) => {
            const domain = cell.getValue() as string;
            if (!domain) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Chip
                label={domain}
                size="small"
                variant="outlined"
                color="info"
                sx={{ fontSize: "0.75rem" }}
              />
            );
          },
        },
        {
          accessorKey: "language",
          header: "Language",
          size: 80,
          Cell: ({ cell }) => {
            const language = cell.getValue() as string;
            if (!language) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Chip
                label={language.toUpperCase()}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: "0.75rem" }}
              />
            );
          },
        },
        {
          accessorKey: "authors",
          header: "Author",
          size: 200,
          Cell: ({ cell }) => {
            const authors = cell.getValue() as string[] | string;
            let authorsText = "";

            if (Array.isArray(authors)) {
              authorsText = authors.join(", ");
            } else {
              authorsText = String(authors || "");
            }

            return <Typography variant="body2">{authorsText}</Typography>;
          },
        },
        {
          accessorKey: "portfolio_notes",
          header: "Notes",
          size: 180,
          Cell: ({ row }) => {
            const notes = row.original.portfolio_notes;
            return (
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontStyle: notes ? "normal" : "italic",
                  color: notes ? "text.primary" : "text.secondary",
                }}
              >
                {notes || ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "portfolio_labels",
          header: "Portfolio Labels",
          size: 140,
          Cell: ({ row }) => {
            const labels = row.original.portfolio_labels || [];
            if (labels.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.slice(0, 2).map((label: string, index: number) => (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    variant="filled"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                ))}
                {labels.length > 2 && (
                  <Chip
                    label={`+${labels.length - 2}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem" }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "portfolio_added_date",
          header: "Added",
          size: 90,
          Cell: ({ row }) => {
            return (
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                {row.original.portfolio_added_date
                  ? new Date(row.original.portfolio_added_date)
                      .toISOString()
                      .split("T")[0]
                  : ""}
              </Typography>
            );
          },
        },
      );
    }

    return baseColumns;
  }, [activeTab, handleEntityClick]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          width: "100%",
          backgroundColor: "background.paper",
        }}
      >
        <LogoLoader size="view" surface="paper" label="Loading the portfolio" />
      </Box>
    );
  }

  if (!portfolio) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: "100vh",
          width: "100%",
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="h6" color="error">
          Portfolio not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: "#1e1e1e",
      }}
      onClick={handlePageClick}
    >
      {/* Filter Sidebar */}
      <PortfolioSidebar />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#1e1e1e",
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="h4">{portfolio.name}</Typography>
              {(portfolio.user_permission === "OWNER" ||
                portfolio.user_permission === "EDITOR") && (
                <Tooltip title="Edit portfolio">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditName(portfolio.name);
                      setEditDescription(portfolio.description || "");
                      setEditDialogOpen(true);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {portfolio.description && (
              <Typography variant="body1" color="text.secondary">
                {portfolio.description}
              </Typography>
            )}
          </Box>

          {/* Controls Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Entity Type Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {entityTypes.map((entityType, index) => (
                <Tab
                  key={entityType.key}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">
                        {entityType.label}
                      </Typography>
                      <Chip
                        label={entityCounts[entityType.key] || 0}
                        size="small"
                        variant="filled"
                        sx={{
                          backgroundColor: entityCounts[entityType.key]
                            ? entityType.color
                            : "grey",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "24px",
                          height: "20px",
                          fontSize: "0.75rem",
                          "& .MuiChip-label": {
                            px: 0.75,
                          },
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <BulkAddToPortfolioButton
                selectedEntities={
                  Object.keys(selectedRows)
                    .filter((id) => selectedRows[id])
                    .map((selectedId) => {
                      const result = enrichedData.find(
                        (r) => r.id === selectedId,
                      );
                      if (!result) return null;

                      // Get entity type from current tab
                      const currentEntityType = entityTypes[activeTab].key;

                      return {
                        id: result.id,
                        name: result.name,
                        type: currentEntityType,
                      };
                    })
                    .filter(Boolean) as Array<{
                    id: string;
                    name: string;
                    type: "organization" | "facility" | "project" | "source";
                  }>
                }
                onSuccess={() => {
                  // Clear selection after successful bulk add
                  setSelectedRows({});
                }}
              />

              <BulkEditPortfolioItemsButton
                portfolioId={portfolioId!}
                selectedItems={
                  Object.keys(selectedRows)
                    .filter((id) => selectedRows[id])
                    .map((selectedEntityId) => {
                      // Find the portfolio item that matches this entity ID
                      const portfolioItem = allItems.find(
                        (item) => item.entity_id === selectedEntityId,
                      );
                      const enrichedEntity = enrichedData.find(
                        (r) => r.id === selectedEntityId,
                      );

                      if (!portfolioItem || !enrichedEntity) return null;

                      return {
                        id: portfolioItem.id, // Use portfolio item ID, not entity ID
                        name: enrichedEntity.name,
                        type: entityTypes[activeTab].key,
                        notes: portfolioItem.notes,
                        labels: portfolioItem.labels,
                        pin_rank: portfolioItem.pin_rank,
                      };
                    })
                    .filter(Boolean) as Array<{
                    id: string;
                    name: string;
                    type: string;
                    notes?: string;
                    labels?: string[];
                    pin_rank?: number;
                  }>
                }
                onSuccess={async () => {
                  // Clear selection and refresh data
                  setSelectedRows({});
                  loadItemsForTab(activeTab);

                  // Refresh entity counts
                  summaryQuery.refetch();
                }}
              />

              <BulkDeleteButton
                portfolioId={portfolioId!}
                selectedItems={
                  Object.keys(selectedRows)
                    .filter((id) => selectedRows[id])
                    .map((selectedEntityId) => {
                      // Find the portfolio item that matches this entity ID
                      const portfolioItem = allItems.find(
                        (item) => item.entity_id === selectedEntityId,
                      );
                      const enrichedEntity = enrichedData.find(
                        (r) => r.id === selectedEntityId,
                      );

                      if (!portfolioItem || !enrichedEntity) return null;

                      return {
                        id: portfolioItem.id, // Use portfolio item ID, not entity ID
                        name: enrichedEntity.name,
                        type: entityTypes[activeTab].key,
                      };
                    })
                    .filter(Boolean) as Array<{
                    id: string;
                    name: string;
                    type: string;
                  }>
                }
                onSuccess={async () => {
                  // Clear selection and refresh data
                  setSelectedRows({});
                  loadItemsForTab(activeTab);

                  // Refresh entity counts
                  summaryQuery.refetch();
                }}
              />

              {/* <Tooltip title="Share portfolio">
                <IconButton onClick={() => setShareDialogOpen(true)}>
                  <ShareIcon />
                </IconButton>
              </Tooltip> */}
            </Box>
          </Box>
        </Paper>

        {/* Results Table */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {loadingTabData ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "400px",
                backgroundColor: "background.paper",
              }}
            >
              <LogoLoader size="panel" surface="paper" label="Loading the companies" />
            </Box>
          ) : enrichedData.length > 0 ? (
            <MaterialReactTable
              columns={getHydratedColumns}
              data={enrichedData.slice(
                tablePagination.pageIndex * tablePagination.pageSize,
                (tablePagination.pageIndex + 1) * tablePagination.pageSize,
              )}
              getRowId={(row) => row.id} // Use entity ID instead of row index
              enableSorting={false}
              enableColumnFilters={false}
              enableGlobalFilter={false}
              enablePagination={true}
              enableRowSelection={true}
              enableSelectAll={true}
              enableColumnResizing={true}
              columnResizeMode="onChange"
              enableStickyHeader={true}
              enableBottomToolbar={true}
              enableTopToolbar={false}
              layoutMode="grid"
              // Manual pagination configuration
              manualPagination={true}
              rowCount={allItems.length}
              state={{
                pagination: tablePagination,
                isLoading: loadingTabData,
                rowSelection: selectedRows,
              }}
              onPaginationChange={handleTablePaginationChange}
              onRowSelectionChange={setSelectedRows}
              initialState={{
                density: "compact",
              }}
              muiTableProps={{
                sx: {
                  "& .MuiTableHead-root": {
                    backgroundColor: "background.paper",
                  },
                  "& .MuiTableHead-root .MuiTableRow-root": {
                    backgroundColor: "background.paper",
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: "background.paper",
                  },
                  "& .MuiTableBody-root": {
                    backgroundColor: "background.paper",
                  },
                  "& .MuiTableFooter-root": {
                    backgroundColor: "background.paper",
                  },
                },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  backgroundColor: "background.paper",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  backgroundColor: "background.paper",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  backgroundColor: "background.paper",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                height: "100%",
                backgroundColor: "background.paper",
              }}
            >
              <Typography color="text.secondary">
                No {entityTypes[activeTab].label.toLowerCase()} in this
                portfolio
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Entity Detail Overlay */}
      {searchParams.get("detail_entity_id") && (
        <EntityDetailOverlay
          entityId={searchParams.get("detail_entity_id")!}
          onClose={handleCloseEntityDetail}
          onEntityClick={handleEntityNavigation}
          onOpenInGraph={handleOpenInGraph}
          onOpenInMap={handleOpenInMap}
        />
      )}

      {/* Edge Detail Overlay */}
      {searchParams.get("detail_relationship_id") && (
        <EdgeDetailOverlay
          relationshipId={searchParams.get("detail_relationship_id")!}
          onClose={handleCloseRelationshipDetail}
          onEntityClick={handleEntityNavigation}
        />
      )}

      {/* Edit Portfolio Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Portfolio</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Portfolio Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={!editName.trim()}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfolioView;
